import { Component, Suspense, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { Grid, Html, Line, OrbitControls } from '@react-three/drei';
import SkeletonStickFigure, { useSkeletonColors } from '../../SkeletonStickFigure';
import { resolveJointName } from '../../skeletonConstants';
import { useTheme, useTokenColors } from '../../ThemeContext';
import { PLAYERS } from '../../charts/palette';
import './skeleton.css';

/**
 * SkeletonView — the three.js viewport behind the design's "SkeletonView":
 * render-backdrop background, token grid, small XYZ axes at the origin, orbit + zoom.
 *
 * bodies: [{ id, joints: { name: {x,y,z} }, slot?: number }]
 *   One SkeletonStickFigure per body. A body with a slot is drawn in its player colour with a
 *   P-label over the head (colour is never shown without its label); otherwise neutral.
 * trails: [{ points: [[x,y,z], …], slot?, color? }] — trajectory lines (e.g. right hand).
 * mode: 'pose' | 'trajectory' | 'location' (location = position trail + current marker + bounds).
 * origin: [x, z] — subtract from every point so the person stands at the grid centre.
 */
class GLBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }
    static getDerivedStateFromError() {
        return { failed: true };
    }
    render() {
        if (this.state.failed) return <div className="egsk-fallback">3D view unavailable — WebGL didn’t start in this browser.</div>;
        return this.props.children;
    }
}

const AXIS_LEN = 0.3;
const offset = (p, o) => (p ? [p[0] - o[0], p[1], p[2] - o[1]] : p);

function Axes() {
    const t = useTokenColors(['axis-x', 'axis-y', 'axis-z']);
    return (
        <group>
            <Line points={[[0, 0.001, 0], [AXIS_LEN, 0.001, 0]]} color={t['axis-x']} lineWidth={2} />
            <Line points={[[0, 0.001, 0], [0, AXIS_LEN, 0]]} color={t['axis-y']} lineWidth={2} />
            <Line points={[[0, 0.001, 0], [0, 0.001, AXIS_LEN]]} color={t['axis-z']} lineWidth={2} />
        </group>
    );
}

function headOf(joints) {
    let head = null;
    Object.entries(joints || {}).forEach(([n, p]) => {
        const c = resolveJointName(n);
        if (c === 'Head' && p) head = p;
    });
    return head;
}

function shiftJoints(joints, o) {
    if (!o || (!o[0] && !o[1])) return joints;
    const out = {};
    Object.entries(joints || {}).forEach(([n, p]) => {
        if (p) out[n] = { x: p.x - o[0], y: p.y, z: p.z - o[1] };
    });
    return out;
}

function Scene({ bodies, trails, mode, origin, colors, theme, location }) {
    const pal = PLAYERS[theme] || PLAYERS.light;
    const grid = useTokenColors(['grid-cell', 'grid-section', 'border-strong', 'accent', 'text-secondary']);
    const o = origin || [0, 0];
    return (
        <>
            <Grid args={[4, 4]} position={[0, 0, 0]} cellSize={0.25} cellThickness={0.6} cellColor={grid['grid-cell']} sectionSize={1} sectionThickness={1} sectionColor={grid['grid-section']} fadeDistance={14} fadeStrength={1.5} infiniteGrid={false} />
            <Axes />
            {mode === 'location' && location ? (
                <group>
                    {[[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([x, z], i) => (
                        <Line key={i} points={[[x, 0, z], [x, 1.8, z]]} color={grid['border-strong']} lineWidth={1} dashed dashSize={0.06} gapSize={0.08} />
                    ))}
                    <Line points={[[-1, 1.8, -1], [1, 1.8, -1], [1, 1.8, 1], [-1, 1.8, 1], [-1, 1.8, -1]]} color={grid['border-strong']} lineWidth={1} dashed dashSize={0.06} gapSize={0.08} />
                    {location.points.length > 1 ? <Line points={location.points.map((p) => offset(p, o))} color={colors.accent} lineWidth={2} /> : null}
                    {location.points.length ? (() => {
                        const p = offset(location.points[location.points.length - 1], o);
                        return (
                            <group>
                                <Line points={[p, [p[0], 0, p[2]]]} color={grid['text-secondary']} lineWidth={1} dashed dashSize={0.03} gapSize={0.04} />
                                <mesh position={p}>
                                    <boxGeometry args={[0.06, 0.06, 0.06]} />
                                    <meshBasicMaterial color={grid.accent} />
                                </mesh>
                            </group>
                        );
                    })() : null}
                </group>
            ) : null}
            {mode === 'trajectory' ? (trails || []).map((tr, i) => (
                tr.points.length > 1 ? <Line key={'t' + i} points={tr.points.map((p) => offset(p, o))} color={tr.color || (tr.slot ? pal[(tr.slot - 1) % 6] : colors.trail)} lineWidth={2} /> : null
            )) : null}
            {mode !== 'location' ? (bodies || []).map((b) => {
                const joints = shiftJoints(b.joints, o);
                const pc = b.slot ? pal[(b.slot - 1) % 6] : null;
                const head = b.slot ? headOf(joints) : null;
                return (
                    <group key={b.id ?? 0}>
                        <SkeletonStickFigure joints={joints} color={pc} />
                        {head ? (
                            <Html position={[head.x, head.y + 0.22, head.z]} center zIndexRange={[10, 0]}>
                                <span className="eg-ptag" style={{ background: pc, borderColor: pc, color: 'var(--egdl-player-ink)' }}>P{b.slot}</span>
                            </Html>
                        ) : null}
                    </group>
                );
            }) : null}
        </>
    );
}

export default function SkeletonView({ bodies = [], trails = [], mode = 'pose', height = 360, origin, location, emptyText, cameraTarget = [0, 0.9, 0] }) {
    const { theme } = useTheme();
    const sk = useSkeletonColors();
    const colors = useMemo(() => ({ trail: sk.token('series-2'), accent: sk.token('accent') || sk.hand }), [sk]);
    const empty = mode === 'location' ? !(location && location.points.length) : !bodies.some((b) => b.joints && Object.keys(b.joints).length);
    return (
        <div className="egsk" style={{ height }}>
            <GLBoundary>
                <Canvas
                    camera={{ position: [1.5, 1.6, 2.6], fov: 45 }}
                    gl={{ antialias: true, alpha: true, failIfMajorPerformanceCaveat: false, powerPreference: 'default' }}
                    dpr={[1, 2]}
                    aria-label="3D skeleton viewport"
                >
                    <Suspense fallback={null}>
                        <Scene bodies={bodies} trails={trails} mode={mode} origin={origin} colors={colors} theme={theme} location={location} />
                    </Suspense>
                    <OrbitControls makeDefault target={cameraTarget} enableDamping dampingFactor={0.08} minDistance={0.8} maxDistance={8} />
                </Canvas>
            </GLBoundary>
            {empty && emptyText ? <span className="egsk-empty">{emptyText}</span> : null}
        </div>
    );
}

/** Pelvis (or chest) position of a joint map, as [x, y, z] — used for origin and trails. */
export function rootOf(joints) {
    let best = null;
    Object.entries(joints || {}).forEach(([n, p]) => {
        const c = resolveJointName(n);
        if (!p) return;
        if (c === 'Pelvis') best = p;
        else if (!best && (c === 'SpineNaval' || c === 'SpineChest')) best = p;
    });
    return best ? [best.x, best.y, best.z] : null;
}

/** Position of one canonical joint in a joint map, as [x, y, z]. */
export function jointOf(joints, canonical) {
    let hit = null;
    Object.entries(joints || {}).forEach(([n, p]) => {
        if (p && resolveJointName(n) === canonical) hit = p;
    });
    return hit ? [hit.x, hit.y, hit.z] : null;
}
